import { unwrapResult } from "@reduxjs/toolkit";
import AddLinkIcon from "@mui/icons-material/AddLink";
import LaunchIcon from "@mui/icons-material/Launch";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RuleIcon from "@mui/icons-material/Rule";
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APIError, AppId } from "../../../../app/appTypes";
import { LpField } from "../../../../app/components/LpField";
import { useAppDispatch } from "../../../../app/hooks";
import { ObjectValidationStatusChip } from "../../../computerizedSystems/components/ObjectValidationStatusChip";
import { LinkValidationProjectComputerizedSystemDialog, LinkValidationProjectComputerizedSystemDialogProps } from "../dialogs/LinkValidationProjectComputerizedSystemDialog";
import {
    loadValidationProjectComputerizedSystems,
    unlinkValidationProjectComputerizedSystem,
    ValidationProjectComputerizedSystemDto,
} from "../validationProjectSlice";
import { useEntityNavigation } from "../../../../app/components/EntityBreadcrumbs";

type DialogState = { tag: "none" } | { tag: "linkComputerizedSystem", props: LinkValidationProjectComputerizedSystemDialogProps };

interface ProjectComputerizedSystemsTabProps {
    projectId: AppId;
}

export const ProjectComputerizedSystemsTab = ({ projectId }: ProjectComputerizedSystemsTabProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const navigateToEntity = useEntityNavigation({ type: "project", id: projectId });
    const [computerizedSystems, setComputerizedSystems] = useState<ValidationProjectComputerizedSystemDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<APIError | null>(null);
    const [dialog, setDialog] = useState<DialogState>({ tag: "none" });
    const [unlinkingComputerizedSystemId, setUnlinkingComputerizedSystemId] = useState<AppId | null>(null);

    useEffect(() => {
        setLoading(true);
        dispatch(loadValidationProjectComputerizedSystems(projectId))
            .then(unwrapResult)
            .then((result) => {
                setComputerizedSystems(result);
                setError(null);
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [dispatch, projectId]);

    const handleUnlinkComputerizedSystemClick = (computerizedSystem: ValidationProjectComputerizedSystemDto) => {
        setUnlinkingComputerizedSystemId(String(computerizedSystem.id));
        dispatch(unlinkValidationProjectComputerizedSystem({ projectId, computerizedSystemId: String(computerizedSystem.computerizedSystemId) }))
            .then(unwrapResult)
            .then(() => {
                setComputerizedSystems((current) => current.filter((item) => item.computerizedSystemId !== computerizedSystem.computerizedSystemId));
                setError(null);
            })
            .catch((err) => setError(err))
            .finally(() => setUnlinkingComputerizedSystemId(null));
    };

    const columns = useMemo<GridColDef[]>(() => [
        {
            field: "order",
            headerName: "Lp",
            width: 80,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LpField value={params.value ?? ""} />
                </Box>
            ),
        },
        { field: "name", headerName: "Name", flex: 1, minWidth: 260 },
        {
            field: "status",
            headerName: "Status",
            width: 180,
            renderCell: (params) => <ObjectValidationStatusChip status={params.value} size="small" />,
        },
        {
            field: "validationProcess",
            headerName: "Czy trwa proces walidacyjny",
            width: 220,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                params.row.isValidationProcessInProgress
                    ? <Chip size="small" color="warning" label="Tak" />
                    : <Chip size="small" variant="outlined" label="Nie" />
            ),
        },
        {
            field: "actions",
            headerName: "Akcje",
            width: 160,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Tooltip title="Przejdź do systemu skomputeryzowanego">
                            <IconButton size="small" onClick={() => navigate(`/computerized-systems/edit/${params.row.computerizedSystemId}`)}>
                                <LaunchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {params.row.validationProcess != null && (
                            <Tooltip title="Przejdź do procesu walidacyjnego">
                                <IconButton size="small" color="primary" onClick={() => navigateToEntity({ type: "process", id: String(params.row.validationProcess.id) })}>
                                    <RuleIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Odepnij system skomputeryzowany z projektu">
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={unlinkingComputerizedSystemId === params.row.id}
                                    onClick={() => handleUnlinkComputerizedSystemClick(params.row as ValidationProjectComputerizedSystemDto)}>
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>
            ),
        },
    ], [navigate, navigateToEntity, unlinkingComputerizedSystemId]);

    return <Paper variant="outlined" sx={{ p: 2 }}>
        {error && <Alert severity="error">{error.kind === "http" ? error.problem?.title ?? "Request failed" : "Request failed"}</Alert>}
        <DataGrid
            autoHeight
            loading={loading}
            rows={computerizedSystems}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
        />
        <Button size="small" variant="outlined" color="primary" startIcon={<AddLinkIcon />} sx={{ mt: 2 }} onClick={() => setDialog({
            tag: "linkComputerizedSystem",
            props: {
                projectId,
                onSave: (computerizedSystem) => {
                    setComputerizedSystems((current) => {
                        const index = current.findIndex((item) => item.id === computerizedSystem.id);
                        if (index >= 0) {
                            const next = [...current];
                            next[index] = computerizedSystem;
                            return next;
                        }
                        return [...current, computerizedSystem];
                    });
                    setDialog({ tag: "none" });
                },
                onCancel: () => setDialog({ tag: "none" }),
            },
        })}>
            Podlinkuj system skomputeryzowany
        </Button>
        {dialog.tag === "linkComputerizedSystem" && <LinkValidationProjectComputerizedSystemDialog {...dialog.props} />}
    </Paper>;
};